/**
 * Cascade resolution: (stylesheets × node classes/ancestry × pseudo states) + inline → one
 * StyleDecls bag. Specificity = total (class + pseudo) count across ALL compounds, ties broken
 * by source order; inline wins last. Descendant combinators match CSS-style: subject on the
 * node, each ancestor compound greedily up the parent chain.
 */
import {
  LAYOUT_KEYS,
  TEXT_MEASURE_KEYS,
  type PseudoState,
  type SelectorCompound,
  type StyleDecls,
  type StyleSheetData,
} from '../types';

/** The slice of UINode the cascade needs (structural — UINode satisfies it). */
export interface StyleTarget {
  classes: readonly string[];
  states: ReadonlySet<PseudoState>;
  parent: StyleTarget | null;
}

function compoundMatches(c: SelectorCompound, node: StyleTarget): boolean {
  return c.classes.every((cls) => node.classes.includes(cls)) && c.pseudo.every((p) => node.states.has(p));
}

/** Ancestors are OUTERMOST FIRST — match them innermost-first, walking up greedily. */
function ancestorsMatch(ancestors: readonly SelectorCompound[], node: StyleTarget): boolean {
  let cursor = node.parent;
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const compound = ancestors[i]!;
    while (cursor && !compoundMatches(compound, cursor)) cursor = cursor.parent;
    if (!cursor) return false;
    cursor = cursor.parent;
  }
  return true;
}

export function resolveStyle(
  sheets: readonly StyleSheetData[],
  node: StyleTarget,
  inline: StyleDecls | null,
): StyleDecls {
  const matched: { spec: number; order: number; decls: StyleDecls }[] = [];
  let order = 0;
  for (const sheet of sheets) {
    for (const rule of sheet.rules) {
      order++;
      if (!rule.classes.every((c) => node.classes.includes(c))) continue;
      if (!rule.pseudo.every((p) => node.states.has(p))) continue;
      if (rule.ancestors && !ancestorsMatch(rule.ancestors, node)) continue;
      const spec =
        rule.classes.length +
        rule.pseudo.length +
        (rule.ancestors?.reduce((n, a) => n + a.classes.length + a.pseudo.length, 0) ?? 0);
      matched.push({ spec, order, decls: rule.decls });
    }
  }
  matched.sort((a, b) => a.spec - b.spec || a.order - b.order);

  const out: StyleDecls = {};
  for (const m of matched) Object.assign(out, m.decls);
  if (inline) Object.assign(out, inline);
  return out;
}

/** Deep-enough equality for style values: primitives, arrays (colors) and plain JSON objects
 *  (calc dims, shadows, gradients). Style values are small — recursion is cheap. */
export function valueEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => valueEq(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => valueEq((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

function keysDiffer(prev: StyleDecls, next: StyleDecls, keys: readonly (keyof StyleDecls)[]): boolean {
  for (const k of keys) {
    if (!valueEq(prev[k], next[k])) return true;
  }
  return false;
}

/** Whether the change between two resolved styles affects flexbox layout. */
export function layoutChanged(prev: StyleDecls, next: StyleDecls): boolean {
  return keysDiffer(prev, next, LAYOUT_KEYS);
}

/** Whether the change affects text measurement (re-measure needed). */
export function textMeasureChanged(prev: StyleDecls, next: StyleDecls): boolean {
  return keysDiffer(prev, next, TEXT_MEASURE_KEYS);
}
